import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  GoogleAuthProvider,
  initializeAuth,
  type Auth,
} from 'firebase/auth';

/**
 * Firebase is only used for authentication here.
 *
 * The console has no Firestore client: `firestore.rules` denies all direct
 * access, and every read goes through the console API so the audit trail and
 * the "rollups only" reporting boundary cannot be bypassed by a browser tab.
 */

const requestedAuthMode = (import.meta.env.VITE_AUTH_MODE ?? 'firebase') as 'firebase' | 'emulator';

/** localhost, 127.0.0.1 or ::1 — anywhere Vite's dev server can actually be. */
function servedLocally(): boolean {
  if (typeof window === 'undefined') return true;
  return /^(localhost|127\.0\.0\.1|\[::1\]|::1)$/.test(window.location.hostname);
}

/**
 * `emulator` skips the identity provider entirely and sends an `x-dmn-dev-user`
 * header, so it must never survive into a deployed console.
 *
 * It is refused off localhost rather than merely discouraged, because Vite
 * loads `.env.local` in *every* mode — including a production build. A
 * developer running `npm run build` locally therefore bakes their own
 * `VITE_AUTH_MODE=emulator` into the bundle, and the deployed console renders a
 * "continue as local developer" button against a real project. The API refuses
 * the header (`DMN_AUTH_MODE=firebase` there), so the failure is a confusing
 * "Missing bearer token" rather than a breach — but the login screen has no
 * business offering it. Mirrors env.ts refusing `insecure-dev` under
 * NODE_ENV=production.
 */
export const AUTH_MODE: 'firebase' | 'emulator' =
  requestedAuthMode === 'emulator' && !servedLocally() ? 'firebase' : requestedAuthMode;

/** In emulator mode the console never contacts Firebase at all. */
export const DEV_USER = import.meta.env.VITE_DEV_USER ?? 'dev@getdeardoc.com';

/**
 * Exercises the sponsor portal shell locally without a real Firebase Auth
 * emulator token — the dev counterpart of the API's `x-dmn-sponsor` header
 * (`consoleAuth.ts`). Unset by default, so `npm run dev` still opens as an
 * operator; set `VITE_DEV_SPONSOR=spo_test` to see the portal instead.
 */
export const DEV_SPONSOR = import.meta.env.VITE_DEV_SPONSOR ?? null;

interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let config: FirebaseWebConfig | null = null;

/** Build-time config. Only set locally — see `loadFirebaseConfig`. */
function configFromEnv(): FirebaseWebConfig | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  if (!apiKey || !projectId) return null;

  return {
    apiKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? `${projectId}.firebaseapp.com`,
    projectId,
  };
}

/**
 * Resolves the Firebase web config once, before the app renders.
 *
 * Deployed builds deliberately do **not** bake this in. CI builds the console
 * exactly once and `promote.yml` ships that same bundle to production, so a
 * `VITE_FIREBASE_*` value compiled into it would point production users at
 * whichever project happened to build it — they would authenticate against
 * staging and every console call would 401. Instead the config is read at
 * runtime from `/__/firebase/init.json`, which Firebase Hosting serves on every
 * site scoped to that site's own project. One bundle, correct in both.
 *
 * `VITE_FIREBASE_*` still wins when set, because local development is not
 * served by Hosting and has no `/__/firebase` to read.
 */
export async function loadFirebaseConfig(): Promise<void> {
  if (AUTH_MODE === 'emulator' || config) return;

  config = configFromEnv();
  if (config) return;

  const response = await fetch('/__/firebase/init.json');
  if (!response.ok) {
    throw new Error(
      `Firebase auth is not configured: /__/firebase/init.json returned ${response.status}. ` +
        'Set VITE_FIREBASE_* for a non-Hosting deployment, or VITE_AUTH_MODE=emulator locally.'
    );
  }

  const loaded = (await response.json()) as Partial<FirebaseWebConfig>;
  if (!loaded.apiKey || !loaded.projectId) {
    throw new Error('Firebase auth is not configured: /__/firebase/init.json is missing fields.');
  }

  config = {
    apiKey: loaded.apiKey,
    authDomain: loaded.authDomain ?? `${loaded.projectId}.firebaseapp.com`,
    projectId: loaded.projectId,
  };
}

export function getFirebaseAuth(): Auth {
  if (auth) return auth;

  // Stays synchronous on purpose: it is called from render effects and from
  // every API request's header builder. `loadFirebaseConfig` runs once in
  // main.tsx before the first paint, so by here the answer is already known.
  if (!config) {
    throw new Error(
      'Firebase auth is not configured. Call loadFirebaseConfig() before rendering, ' +
        'or use VITE_AUTH_MODE=emulator for local development.'
    );
  }

  app ??= initializeApp(config);

  /**
   * localStorage persistence rather than the default IndexedDB.
   *
   * Firebase Auth's IndexedDB persistence registers a `visibilitychange`
   * listener that, on `hidden`, sets an internal `isHiding` flag and closes the
   * database — after which `_openDb()` throws `Database is closing/hidden`
   * until a `pageshow` or `visible` event clears it. Opening the Google sign-in
   * popup is enough to background this tab, so the sign-in that *caused* the
   * hide is the one that fails, and the console shows that error under the
   * button. `browserLocalPersistence` has no lifecycle guard, still survives a
   * restart, and still syncs across tabs.
   *
   * `initializeAuth` rather than `getAuth` because persistence has to be chosen
   * before the instance exists; `getAuth` would already have picked IndexedDB.
   */
  auth = initializeAuth(app, {
    persistence: browserLocalPersistence,
    // getAuth wires this up implicitly; initializeAuth does not, and without it
    // signInWithPopup throws auth/argument-error.
    popupRedirectResolver: browserPopupRedirectResolver,
  });

  const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
  if (emulatorHost) {
    connectAuthEmulator(auth, `http://${emulatorHost}`, { disableWarnings: true });
  }

  return auth;
}

/** DearDoc Google SSO, restricted to the org's domain at the provider level. */
export function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ hd: 'getdeardoc.com', prompt: 'select_account' });
  return provider;
}
